import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Link2, Link2Off, Shield } from 'lucide-react';

/**
 * LinkedAccountsPanel — Account Settings module for managing social
 * sign-in providers (Google + Facebook). Renders for any authenticated
 * user regardless of role.
 *
 * Rules mirrored from the backend (see services/social_auth.py):
 *   • Cannot disconnect the ONLY remaining sign-in method.
 *     If the user has no password AND only one linked provider, the
 *     Disconnect button is disabled with an explanation.
 *   • Never displays or modifies the user's password.
 */
const PROVIDERS = [
  { key: 'google',   label: 'Google',   color: 'bg-white text-slate-900 border-slate-300' },
  { key: 'facebook', label: 'Facebook', color: 'bg-[#1877F2] text-white border-[#1877F2]' },
];

const LinkedAccountsPanel = () => {
  const [state, setState] = useState({ has_password: true, providers: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [fbConfigured, setFbConfigured] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [linked, fb] = await Promise.all([
        api.get('/auth/linked-providers'),
        api.get('/auth/social/facebook/status').catch(() => ({ data: { configured: false } })),
      ]);
      setState(linked.data);
      setFbConfigured(!!fb.data?.configured);
    } catch (e) {
      toast.error('Failed to load linked accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const disconnect = async (provider) => {
    setBusy(provider);
    try {
      await api.delete(`/auth/linked-providers/${provider}`);
      toast.success(`${provider === 'google' ? 'Google' : 'Facebook'} disconnected`);
      await load();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.error(e.response.data.detail ||
          'Cannot disconnect your only sign-in method. Add a password or another provider first.');
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to disconnect');
      }
    } finally {
      setBusy(null);
    }
  };

  const connectGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
    // REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const connectFacebook = () => {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID || '';
    if (!appId) {
      toast.error('Facebook Sign In is not configured on this environment.');
      return;
    }
    const redirectUri = window.location.origin + '/auth/callback';
    const state = crypto.getRandomValues(new Uint32Array(2)).join('-');
    sessionStorage.setItem('fb_oauth_state', state);
    sessionStorage.setItem('fb_oauth_redirect_uri', redirectUri);
    const url = new URL('https://www.facebook.com/v18.0/dialog/oauth');
    url.searchParams.set('client_id', appId);
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('state', state);
    url.searchParams.set('scope', 'email,public_profile');
    url.searchParams.set('response_type', 'code');
    window.location.href = url.toString();
  };

  const linkedProviders = state.providers || [];
  const linkedKeys = new Set(linkedProviders.map(p => p.provider));
  const remainingLoginMethodsAfterDisconnect = (excluded) => {
    const remaining = linkedProviders.filter(p => p.provider !== excluded).length;
    return (state.has_password ? 1 : 0) + remaining;
  };

  return (
    <Card data-testid="linked-accounts-panel">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 className="w-4 h-4 text-cyan-600" />
          Linked sign-in accounts
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-600 inline" /></div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <Shield className="w-3 h-3" />
              Email + password sign-in is <strong>{state.has_password ? 'available' : 'not set up'}</strong> on this account.
            </div>
            {PROVIDERS.map(p => {
              const linked = linkedProviders.find(l => l.provider === p.key);
              const isFbUnavailable = p.key === 'facebook' && !fbConfigured;
              return (
                <div key={p.key}
                  className="flex items-center justify-between p-3 rounded-md border border-slate-200"
                  data-testid={`linked-row-${p.key}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${p.color}`}>
                      {p.label[0]}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 flex items-center gap-2">
                        {p.label}
                        {linked && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px]">Connected</Badge>}
                        {isFbUnavailable && <Badge className="bg-slate-100 text-slate-600 border-slate-200 border text-[10px]">Coming soon</Badge>}
                      </div>
                      {linked ? (
                        <div className="text-xs text-slate-500 truncate">
                          {linked.provider_email}
                          {' · linked '}
                          {new Date(linked.linked_at).toLocaleDateString()}
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500">Not connected</div>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0">
                    {linked ? (
                      (() => {
                        const remaining = remainingLoginMethodsAfterDisconnect(p.key);
                        const wouldOrphan = remaining === 0;
                        return (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => disconnect(p.key)}
                            disabled={busy === p.key || wouldOrphan}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            title={wouldOrphan
                              ? 'Cannot disconnect your only sign-in method. Add a password or another provider first.'
                              : ''}
                            data-testid={`disconnect-${p.key}-btn`}
                          >
                            {busy === p.key
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <><Link2Off className="w-3.5 h-3.5 mr-1" /> Disconnect</>}
                          </Button>
                        );
                      })()
                    ) : (
                      <Button
                        size="sm"
                        onClick={p.key === 'google' ? connectGoogle : connectFacebook}
                        disabled={isFbUnavailable}
                        className={p.key === 'google'
                          ? 'bg-white border border-slate-300 text-slate-900 hover:bg-slate-50'
                          : 'bg-[#1877F2] text-white hover:bg-[#166FE0]'}
                        data-testid={`connect-${p.key}-btn`}
                      >
                        <Link2 className="w-3.5 h-3.5 mr-1" /> Connect
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            <p className="text-[11px] text-slate-500 italic mt-2">
              Connecting Google or Facebook lets you sign in without a password.
              We match by verified email and never change or replace your existing password.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LinkedAccountsPanel;
